import { UserPermissions } from '@creditsights/api-ui-web';
import { Button, colors, Icon, ICONS, SortableWidgetProps, Widget } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import React, { forwardRef, Fragment } from 'react';
import { NavigateFunction, useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from '../../../Base.constants';
import { useFetchRecentRecChanges } from '../../../hooks/issuers';
import useFetchUserPermissions from '../../../hooks/permissions/useFetchUserPermissions';
import { Filters, ViewChange } from '../../../models';
import { IssuerListDto } from '../../../services/dtos/issuer/issuerList.dto';
import { formatCommonDate } from '../../../utils/dateUtils';
import { navigateToCompaniesList } from '../../../utils/navigationUtils';
import { useGlobalModalContext, MODAL_TYPES } from '../../GlobalModal';
import NoResults from '../../NoResults';
import CompanyTitle from '../common/CompanyTitle';
import styles from './RecentRecChangesWidget.module.scss';

const cx = classNames.bind(styles);

interface RecentRecChangesProps extends SortableWidgetProps {
  watchlistId?: string;
  filters: Filters | null;
  globalFeed?: boolean;
}

const renderRecommendationChange = (viewChange: ViewChange | undefined) => {
  switch (viewChange) {
    case ViewChange.New: {
      return (
        <div className={cx('new')} style={{ color: colors.changesNew }} data-testid="new">
          <span>N</span>
          <span>e</span>
          <span>w</span>
        </div>
      );
    }
    case ViewChange.Up: {
      return <Icon name={ICONS.ArrowUp} height={12} width={12} color={colors.changesUp} dataTestId="arrow-up-icon" />;
    }
    case ViewChange.Down: {
      return (
        <Icon name={ICONS.ArrowDown} height={12} width={12} color={colors.changesDown} dataTestId="arrow-down-icon" />
      );
    }
    default:
      return null;
  }
};

const renderRecentRecChanges = (
  recommendations: IssuerListDto | undefined,
  userPermissions: UserPermissions | undefined,
  navigate: NavigateFunction,
  searchParams: string
) => {
  const isRecommendationChangeVisible = recommendations?.results.some(
    (recommendation) => recommendation.latestRec?.viewChange
  );

  return recommendations && recommendations?.results.length > 0 ? (
    <div className={cx('content')}>
      <div>
        {recommendations.results.map((recommendation) => (
          <Fragment key={recommendation.tagId}>
            <div className={cx('container')} data-testid="recent-rec-changes-items">
              <div className={cx('items')}>
                {isRecommendationChangeVisible && (
                  <div className={cx('icon')}>{renderRecommendationChange(recommendation.latestRec?.viewChange)}</div>
                )}
                <CompanyTitle
                  issuerTag={recommendation.tagId}
                  displayName={recommendation.displayName}
                  weighting={recommendation.latestRec?.weighting}
                  hasLink
                />
              </div>
              <div className={cx('date')} data-testid="recent-rec-changed-date">
                {formatCommonDate(recommendation.latestRec?.changedDate)}
              </div>
            </div>
          </Fragment>
        ))}
      </div>
      <Button onClick={() => navigateToCompaniesList(userPermissions, navigate, searchParams)} className={cx('link')}>
        View more
      </Button>
    </div>
  ) : (
    <NoResults />
  );
};

const RecentRecChangesWidget = (props: RecentRecChangesProps, ref): React.ReactElement => {
  const { watchlistId, filters, globalFeed } = props;
  const { pathname, search } = useLocation();
  const {
    isLoading,
    data: recommendations,
    isRefetching,
    isError,
    refetch,
  } = useFetchRecentRecChanges(pathname === ROUTES.feed, filters, watchlistId);
  const navigate = useNavigate();
  const { data: userPermissions } = useFetchUserPermissions();

  const { showModal } = useGlobalModalContext();

  const searchParams = new URLSearchParams(globalFeed ? search : undefined);
  const allowedFilters = ['region', 'sector', 'creditGrade', 'assetClass'];

  searchParams.forEach((value, key) => {
    if (!allowedFilters.includes(key)) {
      searchParams.delete(key);
    }
  });

  searchParams.set('sortField', 'latestRecommendation.changedDate');
  searchParams.set('sortOrder', 'desc');

  const onClick = () => {
    showModal(MODAL_TYPES.CS_VIEW);
  };

  return (
    <div className={cx('body')}>
      <Widget
        headerTitle="Recent Rec Changes"
        headerTooltip={{ tooltipText: 'More on CS View', onClick }}
        isLoading={isLoading || isRefetching}
        isError={isError}
        onRetryAfterErrorClick={refetch}
        fixedHeight
        dataTestId="recent-rec-changes"
        ref={ref}
        {...props}
      >
        {filters
          ? renderRecentRecChanges(recommendations, userPermissions, navigate, `?${searchParams.toString()}`)
          : undefined}
      </Widget>
    </div>
  );
};

export default forwardRef(RecentRecChangesWidget);
